import { supabase } from '../lib/supabase';

export type EmailNotificationType =
  | 'career_tips'
  | 'weekly_digest'
  | 'job_digest'
  | 'optimization_reminder'
  | 'onboarding_nudge'
  | 'blog';

export type EmailNotificationPreferences = Record<EmailNotificationType, boolean>;

export const EMAIL_NOTIFICATION_TYPES: EmailNotificationType[] = [
  'career_tips',
  'weekly_digest',
  'job_digest',
  'optimization_reminder',
  'onboarding_nudge',
  'blog',
];

const DEFAULT_PREFS: EmailNotificationPreferences = {
  career_tips: true,
  weekly_digest: true,
  job_digest: true,
  optimization_reminder: true,
  onboarding_nudge: true,
  blog: false,
};

function isMissingRpcError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('could not find the function')
    || m.includes('function') && m.includes('does not exist')
    || m.includes('schema cache')
    || m.includes('404')
  );
}

/** Read preferences directly from tables when RPC is not deployed yet. */
async function fetchEmailNotificationPreferencesFromTables(
  userId: string,
): Promise<EmailNotificationPreferences> {
  const [{ data: efp }, { data: optOuts }, { data: userRow }, { data: authUser }] =
    await Promise.all([
      supabase
        .from('email_funnel_preferences')
        .select('career_tips_enabled, weekly_digest, jobs_action_alerts, funnel_enabled')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('email_opt_outs')
        .select('email_type')
        .eq('user_id', userId),
      supabase
        .from('users')
        .select('email_job_digest_enabled')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase.auth.getUser(),
    ]);

  const optedOut = new Set((optOuts ?? []).map((r) => r.email_type));

  const email = authUser.user?.email ?? '';
  let blogEnabled = false;
  if (email) {
    const { data: blogSub } = await supabase
      .from('blog_subscribers')
      .select('status')
      .ilike('email', email)
      .maybeSingle();
    blogEnabled = blogSub?.status === 'active';
  }

  const enabled = (type: EmailNotificationType, legacyFlag: boolean | null | undefined): boolean => {
    if (optedOut.has(type)) return false;
    return legacyFlag ?? (type === 'blog' ? false : true);
  };

  return {
    career_tips: enabled('career_tips', efp?.career_tips_enabled),
    weekly_digest: enabled('weekly_digest', efp?.weekly_digest),
    job_digest: enabled('job_digest', userRow?.email_job_digest_enabled),
    optimization_reminder: enabled('optimization_reminder', efp?.jobs_action_alerts),
    onboarding_nudge: enabled('onboarding_nudge', efp?.funnel_enabled),
    blog: enabled('blog', blogEnabled),
  };
}

export async function fetchEmailNotificationPreferences(): Promise<EmailNotificationPreferences> {
  const { data, error } = await supabase.rpc('get_email_notification_preferences');

  if (!error && data) {
    const prefs = { ...DEFAULT_PREFS };
    for (const row of data as Array<{ email_type: string; enabled: boolean }>) {
      if (EMAIL_NOTIFICATION_TYPES.includes(row.email_type as EmailNotificationType)) {
        prefs[row.email_type as EmailNotificationType] = !!row.enabled;
      }
    }
    return prefs;
  }

  if (error && !isMissingRpcError(error.message)) {
    throw error;
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData.user) {
    throw new Error('You must be signed in to view notification settings.');
  }

  return fetchEmailNotificationPreferencesFromTables(authData.user.id);
}

async function persistPreferenceViaTables(
  userId: string,
  emailType: EmailNotificationType,
  enabled: boolean,
): Promise<void> {
  if (!enabled) {
    const { error: optErr } = await supabase.from('email_opt_outs').upsert(
      {
        user_id: userId,
        email_type: emailType,
        source: 'settings',
        opted_out_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,email_type' },
    );
    if (optErr) throw optErr;
  } else {
    const { error: delErr } = await supabase
      .from('email_opt_outs')
      .delete()
      .eq('user_id', userId)
      .eq('email_type', emailType);
    if (delErr) throw delErr;
  }

  const now = new Date().toISOString();

  switch (emailType) {
    case 'career_tips':
    case 'weekly_digest':
    case 'optimization_reminder':
    case 'onboarding_nudge': {
      const column =
        emailType === 'career_tips' ? 'career_tips_enabled'
        : emailType === 'weekly_digest' ? 'weekly_digest'
        : emailType === 'optimization_reminder' ? 'jobs_action_alerts'
        : 'funnel_enabled';

      const { data: existing } = await supabase
        .from('email_funnel_preferences')
        .select('funnel_enabled, weekly_digest, jobs_action_alerts, career_tips_enabled')
        .eq('user_id', userId)
        .maybeSingle();

      const { error } = await supabase.from('email_funnel_preferences').upsert(
        {
          user_id: userId,
          funnel_enabled: existing?.funnel_enabled ?? true,
          weekly_digest: existing?.weekly_digest ?? true,
          jobs_action_alerts: existing?.jobs_action_alerts ?? true,
          career_tips_enabled: existing?.career_tips_enabled ?? true,
          [column]: enabled,
          updated_at: now,
        },
        { onConflict: 'user_id' },
      );
      if (error) throw error;
      break;
    }
    case 'job_digest': {
      const { error } = await supabase
        .from('users')
        .update({ email_job_digest_enabled: enabled })
        .eq('user_id', userId);
      if (error) throw error;
      break;
    }
    case 'blog': {
      const { data: authData } = await supabase.auth.getUser();
      const email = authData.user?.email;
      if (!email) break;
      const { error } = await supabase
        .from('blog_subscribers')
        .update({
          status: enabled ? 'active' : 'unsubscribed',
          unsubscribed_at: enabled ? null : now,
        })
        .ilike('email', email);
      if (error && error.code !== 'PGRST116') throw error;
      break;
    }
    default:
      break;
  }
}

export async function saveEmailNotificationPreference(
  emailType: EmailNotificationType,
  enabled: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('set_email_notification_preference', {
    p_email_type: emailType,
    p_enabled: enabled,
  });

  if (!error) return;

  if (!isMissingRpcError(error.message)) {
    throw error;
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData.user) {
    throw new Error('You must be signed in to save notification settings.');
  }

  await persistPreferenceViaTables(authData.user.id, emailType, enabled);
}

export async function saveEmailNotificationPreferences(
  prefs: EmailNotificationPreferences,
): Promise<void> {
  const payload = Object.fromEntries(
    EMAIL_NOTIFICATION_TYPES.map((type) => [type, prefs[type]]),
  );

  const { error } = await supabase.rpc('save_email_notification_preferences', {
    p_prefs: payload,
  });

  if (!error) return;

  if (!isMissingRpcError(error.message)) {
    throw error;
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData.user) {
    throw new Error('You must be signed in to save notification settings.');
  }

  for (const type of EMAIL_NOTIFICATION_TYPES) {
    await persistPreferenceViaTables(authData.user.id, type, prefs[type]);
  }
}

export function preferencesEqual(
  a: EmailNotificationPreferences,
  b: EmailNotificationPreferences,
): boolean {
  return EMAIL_NOTIFICATION_TYPES.every((type) => a[type] === b[type]);
}

export function listChangedPreferenceTypes(
  saved: EmailNotificationPreferences,
  draft: EmailNotificationPreferences,
): EmailNotificationType[] {
  return EMAIL_NOTIFICATION_TYPES.filter((type) => saved[type] !== draft[type]);
}
